import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef } from "react";
import { cn } from "@/utils/style.ts";
import Icon, { type IconName } from "./icon.tsx";

const alertVariants = cva("alert", {
    variants: {
        variant: {
            info: "alert--info",
            success: "alert--success",
            warning: "alert--warning",
            danger: "alert--danger",
        },
    },
    defaultVariants: {
        variant: "info",
    },
});

type Variants = VariantProps<typeof alertVariants>;

const variantIcons: Record<NonNullable<Variants["variant"]>, IconName> = {
    info: "info-circle",
    success: "check-circle",
    warning: "warning-circle",
    danger: "forbidden-circle",
};

type AlertProps = ComponentPropsWithRef<"div"> & Variants;

export function Alert({ children, className, variant, ...props }: AlertProps) {
    const iconName = variantIcons[variant || "info"];

    return (
        <div role="alert" {...props} className={alertVariants({ variant, className })}>
            <Icon name={iconName} className="alert__icon" />
            <div className="alert__content">{children}</div>
        </div>
    );
}

type AlertTitleProps = ComponentPropsWithRef<"h5">;

export function AlertTitle({ children, className, ...props }: AlertTitleProps) {
    return (
        <h5 {...props} className={cn("alert__title", className)}>
            {children}
        </h5>
    );
}

type AlertDescriptionProps = ComponentPropsWithRef<"p">;

export function AlertDescription({ children, className, ...props }: AlertDescriptionProps) {
    return (
        <p {...props} className={cn("alert__description", className)}>
            {children}
        </p>
    );
}
